import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, FileSearch, Upload, Trash2, Download, Brain, CheckCircle2,
  AlertCircle, AlertTriangle, RefreshCw, X, Save, Edit2, Plus, Loader2,
  FileText, FileSpreadsheet, Presentation, Send, Bot, User, Clock,
  Phone, Mail, Building2, Euro, TrendingUp, Target, ChevronDown, ChevronUp, ChevronRight,
  ExternalLink, Tag, Users, Calendar, Trophy, Zap, Shield, Copy, Check,
  MessageSquare, ClipboardList, BarChart2, BookOpen, Layers, Settings2, Eye,
  Sparkles, TreePine, Archive, Trophy as TrophyIcon, ShieldCheck, Package
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { KpiBox } from "../components/exec_summary/KpiBox";
import { ComposicionOferta } from "../components/exec_summary/ComposicionOferta";
import { CurvaEconomica } from "../components/exec_summary/CurvaEconomica";
import GenerateAIAnalysisModal from "@/components/GenerateAIAnalysisModal";
import LicitacionComplianceModal from "@/components/LicitacionComplianceModal";
import LicitacionDossierModal from "@/components/LicitacionDossierModal";
import { toast } from "sonner";

// ── Helpers ────────────────────────────────────────────────────────────────────
const ESTADOS = [
  { id: "prospecto",          label: "Prospecto"        },
  { id: "documentacion",      label: "Recogiendo docs"  },
  { id: "analisis",           label: "Analizando"       },
  { id: "oferta_preparacion", label: "Prep. oferta"     },
  { id: "presentada",         label: "Presentada"       },
  { id: "negociacion",        label: "Negociación"      },
  { id: "ganada",             label: "GANADA"           },
  { id: "perdida",            label: "Perdida"          },
  { id: "descartada",         label: "Descartada"       },
];
const TIPOS_SERVICIO = ["RSU / Recogida","Limpieza Viaria","Movilidad / ORA","Zonas Verdes","Parques Urbanos","Control Presencia","Mixto","Otro"];
const ACTIVITY_TYPES = [
  { id: "llamada",       label: "Llamada",    icon: Phone   },
  { id: "reunion",       label: "Reunión",    icon: Users   },
  { id: "email",         label: "Email",      icon: Mail    },
  { id: "nota",          label: "Nota",       icon: ClipboardList },
  { id: "oferta_enviada",label: "Oferta enviada", icon: Send },
];
const CAT_LABELS = {
  pliego: "Pliego técnico (PPT)",
  pliego_administrativo: "Pliego administrativo (PCA)",
  anexo: "Anexo / Doc. complementario",
  memoria_proveedor: "Propuesta proveedor",
  excel_economico: "Excel económico",
  otro: "Otro",
};
const FILE_ICON = (nombre) => {
  const ext = (nombre || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return <FileText size={16} className="text-red-500" />;
  if (["xlsx","xls","csv"].includes(ext)) return <FileSpreadsheet size={16} className="text-green-600" />;
  if (["pptx","ppt"].includes(ext)) return <Presentation size={16} className="text-orange-500" />;
  return <FileText size={16} className="text-gray-400" />;
};
function fmtEur(n) { if (!n) return "—"; return new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(n); }
function fmtDate(s) { if (!s) return "—"; return new Date(s).toLocaleDateString("es-ES",{day:"2-digit",month:"short",year:"numeric"}); }
function fmtDateTime(s) { if (!s) return "—"; return new Date(s).toLocaleDateString("es-ES",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}); }

const COBERTURA_CFG = {
  cubierto: { label: "Cubierto",  bg: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500", icon: CheckCircle2 },
  parcial:  { label: "Parcial",   bg: "bg-amber-100 text-amber-800",     dot: "bg-amber-500",   icon: AlertTriangle },
  nuevo:    { label: "Nuevo",     bg: "bg-red-100 text-red-800",         dot: "bg-red-500",     icon: AlertCircle   },
};
const PRIORIDAD_CFG = {
  alta:  "bg-red-100 text-red-700",
  media: "bg-amber-100 text-amber-700",
  baja:  "bg-gray-100 text-gray-600",
};

// ── Pestaña Documentos ─────────────────────────────────────────────────────────
function TabDocumentos({ lic, onUploaded, onDeleted }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [catForm, setCatForm] = useState("pliego");
  const [viewFile, setViewFile] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [dossierOpen, setDossierOpen] = useState(false);
  const fileRef = useRef();

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("categoria", catForm);
        await axios.post(`${API}/licitaciones/${lic.id}/upload`, fd);
      }
      onUploaded();
    } finally { setUploading(false); }
  };

  return (
    <div className="p-5 space-y-5">
      {/* Header with Generate AI button */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FileText size={18} className="text-[#007934]"/>
            Documentos ({(lic.archivos || []).length})
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Pliegos, propuestas, análisis Pando y archivos de la licitación.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setDossierOpen(true)}
            data-testid="open-dossier-btn"
            className="bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600 hover:opacity-90 text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-2 shadow-md"
            title="Dossier 1-click: Compliance + 4 síntesis + RAG histórico en paralelo (~90s)"
          >
            <Package size={14}/> Dossier 1-click
          </button>
          <button
            onClick={() => setComplianceOpen(true)}
            data-testid="open-compliance-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 shadow-sm"
            title="Compliance Check Pando: compara pliego vs propuesta y genera matriz de cumplimiento"
          >
            <ShieldCheck size={14}/> Compliance pliego↔propuesta
          </button>
          <button
            onClick={() => setGenOpen(true)}
            data-testid="open-gen-ai-btn"
            className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 shadow-sm"
            title="Generar un informe Pando personalizado (cumplimiento, TCO, riesgos, etc.)"
          >
            <Brain size={14}/> Generar análisis Pando
          </button>
        </div>
      </div>

      {/* Upload Area */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${dragOver ? "border-[#007934] bg-[#E8F5E9]" : "border-gray-200 bg-gray-50 hover:border-[#007934]/50"}`}
        data-testid="upload-dropzone"
      >
        <Upload size={28} className={`mx-auto mb-3 ${dragOver ? "text-[#007934]" : "text-gray-300"}`} />
        <p className="text-sm font-medium text-gray-700 mb-1">Arrastra archivos aquí o haz clic para seleccionar</p>
        <p className="text-xs text-gray-400 mb-4">PDF, PPT, PPTX, DOCX, XLSX · Máx. 50 MB por archivo</p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <select value={catForm} onChange={e => setCatForm(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#007934]">
            {Object.entries(CAT_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={() => fileRef.current?.click()}
            className="bg-[#007934] text-white px-4 py-1.5 rounded-lg text-xs font-medium hover:bg-[#006528]"
            data-testid="select-files-btn">
            {uploading ? <span className="flex items-center gap-1"><Loader2 size={12} className="animate-spin" />Subiendo...</span> : "Seleccionar archivo"}
          </button>
        </div>
        <input ref={fileRef} type="file" multiple accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.csv,.txt" className="hidden"
          onChange={e => handleFiles(e.target.files)} />
      </div>

      {/* File List */}
      {(lic.archivos || []).length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FileText size={32} className="mx-auto mb-2 opacity-20" />
          <p className="text-sm">Sube el pliego técnico (PPT/PDF), pliego administrativo (PCA), anexos, memorias de proveedores o Excel económicos</p>
        </div>
      ) : (
        <div className="space-y-2">
          {(lic.archivos || []).map(f => (
            <div key={f.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-gray-200" data-testid={`file-${f.id}`}>
              {FILE_ICON(f.nombre)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                  {f.nombre}
                  {f.generated_ai && <span className="bg-purple-100 text-purple-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">Pando</span>}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                  <span className="bg-gray-100 px-1.5 rounded">{CAT_LABELS[f.categoria] || f.categoria}</span>
                  <span>{f.tamanio ? `${(f.tamanio/1024).toFixed(0)} KB` : ""}</span>
                  <span>{fmtDate(f.uploaded_at)}</span>
                  {f.uploaded_by && <span>·  {f.uploaded_by}</span>}
                </div>
                {/* Indicador de indexación Pando */}
                {!f.generated_ai && (
                  <div className="flex items-center gap-2 text-[10px] mt-1 flex-wrap" data-testid={`ai-index-${f.id}`}>
                    {f.pages_count ? (
                      <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">
                        📄 {f.pages_count} pág{f.pages_count !== 1 ? "s" : ""}
                      </span>
                    ) : null}
                    {typeof f.texto_chars === "number" && f.texto_chars > 0 ? (
                      <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-200">
                        ✓ {(f.texto_chars/1000).toFixed(1)}K chars indexados
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">
                        ⚠ Texto no indexado · Pando no lo leerá
                      </span>
                    )}
                    {f.extract_truncated_pages && (
                      <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-1.5 py-0.5 rounded border border-red-200" title={f.extract_note || "Se truncaron páginas"}>
                        ⚠ Truncado
                      </span>
                    )}
                  </div>
                )}
              </div>
              {f.texto_extraido && f.generated_ai && (
                <button
                  onClick={() => setViewFile({ ...f, _licId: lic.id })}
                  className="p-1.5 text-gray-400 hover:text-purple-600"
                  title="Ver análisis Pando"
                  data-testid={`view-${f.id}`}
                >
                  <Eye size={14}/>
                </button>
              )}
              {/* Re-indexar (no aplica a archivos Pando generados) */}
              {!f.generated_ai && (f.storage_path || f.url) && (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Re-indexar "${f.nombre}"? Descargará el archivo y extraerá el texto otra vez.`)) return;
                    try {
                      const { data } = await axios.post(`${API}/licitaciones/${lic.id}/files/${f.id}/reindex`);
                      toast.success(`✓ Re-indexado: ${data.pages ? data.pages + " págs · " : ""}${(data.chars/1000).toFixed(1)}K chars`);
                      onUploaded && onUploaded();
                    } catch (e) {
                      toast.error("Error re-indexando: " + (e.response?.data?.detail || e.message));
                    }
                  }}
                  className="p-1.5 text-gray-400 hover:text-purple-600"
                  title="Re-indexar texto para la Pando"
                  data-testid={`reindex-${f.id}`}
                >
                  <RefreshCw size={14}/>
                </button>
              )}
              {f.url ? (
                <a href={f.url} download target="_blank" rel="noreferrer"
                  className="p-1.5 text-gray-400 hover:text-[#007934]" data-testid={`download-${f.id}`}>
                  <Download size={14} />
                </a>
              ) : (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const r = await axios.get(
                        `${API}/licitaciones/${lic.id}/files/${f.id}/download`,
                        { responseType: "blob" }
                      );
                      const blob = new Blob([r.data], { type: r.headers["content-type"] || f.content_type || "application/octet-stream" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = f.nombre || "archivo";
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      const msg = e?.response?.status === 401
                        ? "Sesión expirada. Vuelve a iniciar sesión."
                        : (e?.response?.data?.detail || "Error descargando archivo");
                      alert(msg);
                    }
                  }}
                  className="p-1.5 text-gray-400 hover:text-[#007934]" data-testid={`download-${f.id}`}
                  title="Descargar archivo"
                >
                  <Download size={14} />
                </button>
              )}
              <button onClick={() => onDeleted(f.id)} className="p-1.5 text-gray-300 hover:text-red-500">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      {viewFile && <MarkdownPreviewModal file={viewFile} onClose={() => setViewFile(null)}/>}
      {genOpen && (
        <GenerateAIAnalysisModal
          open={genOpen}
          lic={lic}
          onClose={() => setGenOpen(false)}
          onGenerated={() => onUploaded()}
        />
      )}
      {complianceOpen && (
        <LicitacionComplianceModal
          open={complianceOpen}
          lic={lic}
          onClose={() => setComplianceOpen(false)}
          onGenerated={() => onUploaded()}
        />
      )}
      {dossierOpen && (
        <LicitacionDossierModal
          open={dossierOpen}
          lic={lic}
          onClose={() => setDossierOpen(false)}
          onGenerated={() => onUploaded()}
        />
      )}
    </div>
  );
}

// ── Markdown Preview Modal (Pando outputs) ──────────────────────────────────
function MarkdownPreviewModal({ file, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="md-preview-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Brain size={16} className="text-purple-600"/>
            <h3 className="text-sm font-bold">{file.nombre}</h3>
            <span className="bg-purple-100 text-purple-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">Pando</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const blob = new Blob([file.texto_extraido || ""], { type: "text/markdown" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = file.nombre;
                a.click(); URL.revokeObjectURL(url);
              }}
              className="text-xs border border-gray-200 px-3 py-1 rounded hover:bg-gray-50 flex items-center gap-1"
              data-testid="md-download-md"
              title="Descargar Markdown (.md)"
            ><Download size={12}/> .md</button>
            <button
              onClick={async () => {
                try {
                  const r = await axios.get(`${API}/licitaciones/${file._licId || (window.location.pathname.split('/').pop())}/files/${file.id}/download`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url; a.download = file.nombre.replace(".md", ".pdf");
                  a.click(); URL.revokeObjectURL(url);
                } catch (e) { alert("Error descargando PDF: " + e.message); }
              }}
              className="text-xs bg-[#007934] text-white px-3 py-1 rounded hover:bg-[#006528] flex items-center gap-1"
              data-testid="md-download-pdf"
              title="Descargar como PDF con portada Valoriza"
            ><Download size={12}/> PDF</button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 prose prose-sm max-w-none prose-headings:text-gray-900 prose-h1:text-2xl prose-h2:text-xl prose-h2:border-b prose-h2:pb-2 prose-table:text-xs prose-th:bg-gray-50 prose-th:p-2 prose-td:p-2 prose-td:border prose-th:border">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{file.texto_extraido || ""}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

// ── Pestaña Análisis Pando ────────────────────────────────────────────────────────
function TabAnalisis({ lic, onAnalyzed, analyzing, setAnalyzing }) {
  const analisis = lic.analisis_ia;

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/licitaciones/${lic.id}/analyze`);
      const tid = data.task_id;
      if (!tid) {
        // Compatibilidad con respuesta síncrona antigua
        onAnalyzed();
        return;
      }
      // Polling cada 3s, hasta 10 min
      const started = Date.now();
      const TIMEOUT_MS = 10 * 60 * 1000;
      while (Date.now() - started < TIMEOUT_MS) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const { data: st } = await axios.get(`${API}/licitaciones/${lic.id}/analyze/status/${tid}`);
          if (st.status === "done") {
            onAnalyzed();
            return;
          }
          if (st.status === "error") {
            alert(st.error || "Error en el análisis");
            return;
          }
        } catch (e) {
          // Sigue intentando — fallos puntuales de polling son tolerables
        }
      }
      alert("El análisis está tardando más de lo esperado. Revisa más tarde la pestaña.");
    } catch (e) {
      alert(e.response?.data?.detail || "Error en el análisis");
    } finally { setAnalyzing(false); }
  };

  if (!analisis) return (
    <div className="p-8 text-center">
      <Brain size={40} className="mx-auto mb-4 text-gray-200" />
      <h3 className="text-base font-bold text-gray-800 mb-2">Análisis Pando pendiente</h3>
      <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">Sube el pliego técnico (PPT), pliego administrativo (PCA) y anexos. La Pando extraerá todas las necesidades IT, KPIs, SLAs y riesgos automáticamente.</p>
      <button onClick={runAnalysis} disabled={analyzing || !(lic.archivos || []).length}
        className="bg-[#007934] text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-[#006528] disabled:opacity-60 flex items-center gap-2 mx-auto"
        data-testid="run-analysis-btn">
        {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
        {analyzing ? "Analizando (puede tardar 30s)…" : "Analizar documentos con Pando"}
      </button>
      {!(lic.archivos || []).length && <p className="text-xs text-amber-600 mt-3">Primero sube al menos un documento en la pestaña Documentos</p>}
    </div>
  );

  return (
    <div className="p-5 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><Brain size={16} className="text-[#007934]" />Análisis Pando del Pliego</h3>
          <p className="text-xs text-gray-400 mt-0.5">Generado el {fmtDateTime(analisis.generated_at)} · {analisis.generated_by}</p>
        </div>
        <button onClick={runAnalysis} disabled={analyzing} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#007934] border border-gray-200 px-3 py-1.5 rounded-lg hover:border-[#007934] transition-all" data-testid="rerun-analysis-btn">
          <RefreshCw size={12} className={analyzing ? "animate-spin" : ""} />{analyzing ? "Analizando…" : "Regenerar"}
        </button>
      </div>

      {/* Resumen ejecutivo */}
      <div className="bg-[#E8F5E9] rounded-xl p-4 border-l-4 border-[#007934]">
        <p className="text-xs font-bold text-[#007934] uppercase tracking-wider mb-1">Resumen Ejecutivo</p>
        <p className="text-sm text-gray-800">{analisis.resumen_ejecutivo}</p>
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-600">
          {analisis.plazo_contrato && <span className="flex items-center gap-1"><Clock size={11} />Plazo: <b>{analisis.plazo_contrato}</b></span>}
          {analisis.importe_estimado && <span className="flex items-center gap-1"><Euro size={11} />Importe: <b>{fmtEur(analisis.importe_estimado)}</b></span>}
        </div>
      </div>

      {/* Necesidades IT */}
      {(analisis.necesidades_it || []).length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2"><Zap size={14} className="text-amber-500" />Necesidades IT identificadas ({analisis.necesidades_it.length})</h4>
          <div className="space-y-2">
            {analisis.necesidades_it.map((n, i) => (
              <div key={`nec-${i}-${n.id || "x"}`} className="bg-white rounded-xl border border-gray-100 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 flex-1">{n.descripcion}</p>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${PRIORIDAD_CFG[n.prioridad] || PRIORIDAD_CFG.media}`}>{n.prioridad}</span>
                    <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium uppercase">{n.tipo}</span>
                  </div>
                </div>
                {n.detalle && <p className="text-xs text-gray-500 mt-1">{n.detalle}</p>}
                {n.coste_estimado_eur && <p className="text-xs text-amber-700 mt-1 font-medium">Coste est.: {fmtEur(n.coste_estimado_eur)}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two column: Tecnologías + KPIs */}
      <div className="grid md:grid-cols-2 gap-4">
        {(analisis.tecnologias_requeridas || []).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1.5"><Layers size={12} />Tecnologías requeridas</h4>
            <div className="flex flex-wrap gap-1.5">
              {analisis.tecnologias_requeridas.map((t, i) => <span key={`tec-${i}`} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">{t}</span>)}
            </div>
          </div>
        )}
        {(analisis.kpis_sla || []).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1.5"><Target size={12} />KPIs y SLAs</h4>
            <div className="space-y-1.5">
              {analisis.kpis_sla.slice(0,5).map((k, i) => (
                <div key={`kpi-${i}`} className="text-xs"><span className="font-medium text-gray-800">{k.nombre}:</span> <span className="text-gray-600">{k.descripcion}</span>{k.umbral && <span className="text-amber-700 font-medium"> ({k.umbral})</span>}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Penalizaciones + Riesgos */}
      <div className="grid md:grid-cols-2 gap-4">
        {(analisis.penalizaciones || []).length > 0 && (
          <div className="bg-red-50 rounded-xl border border-red-100 p-4">
            <h4 className="text-xs font-bold text-red-700 uppercase tracking-wide mb-3 flex items-center gap-1.5"><AlertTriangle size={12} />Penalizaciones</h4>
            {analisis.penalizaciones.slice(0,4).map((p, i) => <div key={`pen-${i}`} className="text-xs text-gray-700 mb-1.5"><b>{p.tipo}:</b> {p.condicion} <span className="text-red-600">({p.importe})</span></div>)}
          </div>
        )}
        {(analisis.riesgos || []).length > 0 && (
          <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
            <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-3 flex items-center gap-1.5"><Shield size={12} />Riesgos identificados</h4>
            {analisis.riesgos.slice(0,4).map((r, i) => (
              <div key={`risk-${i}`} className="text-xs text-gray-700 mb-1.5 flex items-start gap-1.5">
                <span className={`font-bold flex-shrink-0 ${r.nivel==="alto"?"text-red-600":r.nivel==="medio"?"text-amber-600":"text-gray-500"}`}>[{r.nivel}]</span>
                <span>{r.riesgo}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fechas clave */}
      {(analisis.fechas_clave || []).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1.5"><Calendar size={12} />Fechas y plazos clave</h4>
          <div className="grid sm:grid-cols-2 gap-2">
            {analisis.fechas_clave.map((f, i) => <div key={`fec-${i}`} className="text-xs"><span className="font-medium text-gray-800">{f.evento}:</span> <span className="text-[#007934]">{f.plazo}</span></div>)}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Pestaña Cobertura VSM ──────────────────────────────────────────────────────
function TabCobertura({ lic, onAnalyzed, analyzing, setAnalyzing }) {
  const cobertura = lic.cobertura_vsm;
  const analisis = lic.analisis_ia;

  if (!cobertura) return (
    <div className="p-8 text-center">
      <BarChart2 size={40} className="mx-auto mb-4 text-gray-200" />
      <p className="text-sm text-gray-600 mb-2">La cobertura VSM se calcula automáticamente al realizar el análisis Pando.</p>
      {!analisis && <p className="text-xs text-amber-600">Primero completa el análisis Pando en la pestaña anterior.</p>}
    </div>
  );

  const items = cobertura.items || [];
  const res = cobertura.resumen || {};
  const cubiertos = items.filter(i => i.cobertura === "cubierto").length;
  const parciales = items.filter(i => i.cobertura === "parcial").length;
  const nuevos    = items.filter(i => i.cobertura === "nuevo").length;

  return (
    <div className="p-5 space-y-5">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Cobertura directa", val: `${res.pct_cubierto || 0}%`, cls: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Cobertura parcial", val: `${res.pct_parcial || 0}%`, cls: "text-amber-600", bg: "bg-amber-50" },
          { label: "Nuevos a desarrollar", val: `${res.pct_nuevo || 0}%`, cls: "text-red-600", bg: "bg-red-50" },
          { label: "CAPEX estimado gaps", val: res.capex_estimado_total_eur ? fmtEur(res.capex_estimado_total_eur) : "—", cls: "text-blue-600", bg: "bg-blue-50" },
        ].map(k => (
          <div key={k.label} className={`${k.bg} rounded-xl p-3`} data-testid={`cobertura-kpi-${k.label}`}>
            <p className={`text-xl font-bold ${k.cls}`}>{k.val}</p>
            <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Coverage bar */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium">
          <span className="text-emerald-600">{cubiertos} cubiertos</span>
          <span className="text-gray-300">·</span>
          <span className="text-amber-600">{parciales} parciales</span>
          <span className="text-gray-300">·</span>
          <span className="text-red-600">{nuevos} nuevos</span>
        </div>
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${res.pct_cubierto||0}%` }} />
          <div className="h-full bg-amber-400 transition-all" style={{ width: `${res.pct_parcial||0}%` }} />
          <div className="h-full bg-red-400 transition-all" style={{ width: `${res.pct_nuevo||0}%` }} />
        </div>
      </div>

      {/* Items table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-xs" data-testid="cobertura-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {["Necesidad IT", "Cobertura", "App VSM", "Gap / Acción requerida", "Coste est.", "Horas"].map(h => (
                <th key={h} className="px-3 py-2.5 text-left text-gray-600 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const cfg = COBERTURA_CFG[item.cobertura] || COBERTURA_CFG.nuevo;
              const Icon = cfg.icon;
              const need = (analisis?.necesidades_it || []).find(n => n.id === item.need_id);
              return (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                  <td className="px-3 py-2.5 font-medium text-gray-900 max-w-[200px]">{need?.descripcion || item.need_id}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-[10px] ${cfg.bg}`}>
                      <Icon size={10} />{cfg.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[#007934] font-medium">{item.app_vsm || "—"}</td>
                  <td className="px-3 py-2.5 text-gray-600 max-w-[220px]">{item.gap || item.justificacion || "—"}</td>
                  <td className="px-3 py-2.5 font-medium text-red-600">{item.coste_desarrollo_estimado_eur ? fmtEur(item.coste_desarrollo_estimado_eur) : "—"}</td>
                  <td className="px-3 py-2.5 text-gray-600">{item.horas_despliegue_estimadas ? `${item.horas_despliegue_estimadas}h` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recomendación */}
      {res.recomendacion && (
        <div className="bg-[#E8F5E9] rounded-xl p-4 border-l-4 border-[#007934]">
          <p className="text-xs font-bold text-[#007934] uppercase tracking-wider mb-1">Recomendación técnica VSM</p>
          <p className="text-sm text-gray-800">{res.recomendacion}</p>
        </div>
      )}

      {res.opex_mensual_estimado_eur && (
        <div className="flex gap-4 text-sm">
          <div className="bg-white border border-gray-100 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-500">OPEX mensual estimado</p>
            <p className="text-lg font-bold text-amber-600">{fmtEur(res.opex_mensual_estimado_eur)}</p>
          </div>
          {res.horas_despliegue_total_estimadas && (
            <div className="bg-white border border-gray-100 rounded-xl px-4 py-3">
              <p className="text-xs text-gray-500">Horas despliegue estimadas</p>
              <p className="text-lg font-bold text-blue-600">{res.horas_despliegue_total_estimadas}h</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Pestaña Memoria Técnica ────────────────────────────────────────────────────
function TabMemoria({ lic, onGenerated }) {
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [extra, setExtra] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const [data, setData] = useState(null);       // {markdown, versions, updated_at, updated_by_name}
  const [markdown, setMarkdown] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showVersions, setShowVersions] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/licitaciones/${lic.id}/memoria-tecnica`);
      setData(r.data || {});
      setMarkdown(r.data?.markdown || "");
      setDirty(false);
    } catch { /* no-op */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lic.id]);
  useEffect(() => {
    axios.get(`${API}/licitaciones/config/memoria-secciones`)
      .then(r => { setSections(r.data || []); setSelectedSections((r.data || []).map(s => s.key)); })
      .catch(() => {});
  }, []);

  const toggleSection = (key) => setSelectedSections(p => p.includes(key) ? p.filter(s => s !== key) : [...p, key]);
  const selectAll = () => setSelectedSections(sections.map(s => s.key));
  const selectNone = () => setSelectedSections([]);

  const generate = async () => {
    if (selectedSections.length === 0) { toast.error("Selecciona al menos una sección"); return; }
    setGenerating(true);
    try {
      await axios.post(`${API}/licitaciones/${lic.id}/generate-memoria`, {
        secciones: selectedSections,
        extra_instructions: extra,
      });
      await load();
      setShowConfig(false);
      toast.success("Memoria generada");
      onGenerated();
    } catch (e) { toast.error(e.response?.data?.detail || "Error generando memoria"); }
    finally { setGenerating(false); }
  };

  const save = async (snapshot = true) => {
    setSaving(true);
    try {
      await axios.put(`${API}/licitaciones/${lic.id}/memoria-tecnica`, {
        markdown, save_version: snapshot,
      });
      await load();
      toast.success(snapshot ? "Versión guardada" : "Borrador actualizado");
    } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const exportPdf = async () => {
    try {
      // Save current changes first if dirty
      if (dirty) { await save(false); }
      const r = await axios.get(`${API}/licitaciones/${lic.id}/memoria-tecnica/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Memoria_Tecnica_${(lic.titulo||"lic").slice(0,40).replace(/\W+/g,"_")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error exportando PDF: " + (e.response?.data?.detail || e.message)); }
  };

  const restoreVersion = (v) => {
    if (!window.confirm("¿Restaurar esta versión? El contenido actual se guardará como nueva versión.")) return;
    setMarkdown(v.markdown);
    setDirty(true);
    toast.info("Versión cargada en el editor. Guarda para confirmar.");
  };

  const hasContent = !!(markdown && markdown.trim());
  const lineCount = markdown.split("\n").length;
  const wordCount = markdown.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="p-5 space-y-4" data-testid="tab-memoria">
      {/* Top toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><BookOpen size={16} className="text-[#007934]"/> Memoria Técnica — Editor</h3>
          {data?.updated_at && (
            <p className="text-xs text-gray-400 mt-0.5">
              Actualizado {fmtDateTime(data.updated_at)} por <strong>{data.updated_by_name || data.updated_by || "—"}</strong>
              {data.versions?.length > 0 && <span> · <button onClick={() => setShowVersions(v=>!v)} className="text-[#007934] hover:underline">{data.versions.length} versión(es) previas</button></span>}
              {dirty && <span className="ml-2 text-amber-600">• borrador sin guardar</span>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowConfig(v => !v)}
            disabled={!lic.analisis_ia}
            className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:border-[#007934] flex items-center gap-1 disabled:opacity-60"
            data-testid="configure-memoria-btn"
          ><Settings2 size={12}/> Configurar secciones</button>
          <button
            onClick={generate}
            disabled={generating || !lic.analisis_ia || selectedSections.length === 0}
            className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
            data-testid="generate-memoria-btn"
          >{generating ? <Loader2 size={12} className="animate-spin"/> : <Brain size={12}/>} {hasContent ? "Regenerar con Pando" : "Generar con Pando"}</button>
          <button
            onClick={() => save(true)}
            disabled={saving || !dirty}
            className="text-xs bg-[#007934] hover:bg-[#006528] text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
            data-testid="save-memoria-btn"
          >{saving ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Guardar versión</button>
          <button
            onClick={exportPdf}
            disabled={!hasContent}
            className="text-xs border border-[#007934] text-[#007934] hover:bg-[#007934]/5 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
            data-testid="export-memoria-pdf"
          ><Download size={12}/> PDF</button>
        </div>
      </div>

      {!lic.analisis_ia && !hasContent && (
        <div className="text-center py-6 text-amber-600 text-sm bg-amber-50 rounded-xl border border-amber-200">
          <AlertTriangle size={16} className="mx-auto mb-1"/>Primero completa el análisis Pando del pliego para poder generar la memoria automática
        </div>
      )}

      {/* Section picker (collapsible) */}
      {showConfig && lic.analisis_ia && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="memoria-config">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-gray-800">Secciones a redactar ({selectedSections.length}/{sections.length})</h4>
            <div className="flex items-center gap-2">
              <button onClick={selectAll} className="text-xs text-[#007934] hover:underline">Todas</button>
              <span className="text-gray-300">·</span>
              <button onClick={selectNone} className="text-xs text-gray-500 hover:underline">Ninguna</button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
            {sections.map(s => {
              const active = selectedSections.includes(s.key);
              return (
                <button key={s.key} onClick={() => toggleSection(s.key)}
                        className={`text-left p-2.5 rounded-lg border text-xs transition-all ${active ? "bg-[#007934]/5 border-[#007934]" : "bg-white border-gray-200 hover:border-gray-300"}`}>
                  <div className="flex items-start gap-2">
                    <div className={`w-4 h-4 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 ${active ? "bg-[#007934] border-[#007934]" : "bg-white border-gray-300"}`}>
                      {active && <Check size={10} className="text-white"/>}
                    </div>
                    <div className="flex-1">
                      <div className={`font-semibold ${active ? "text-[#007934]" : "text-gray-800"}`}>{s.label}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{s.detalle}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wide">Instrucciones adicionales (opcional)</label>
            <textarea value={extra} onChange={e => setExtra(e.target.value)} rows={2}
                      placeholder="Ej: enfatizar experiencia previa y compromiso de huella carbono"
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs mt-1"/>
          </div>
        </div>
      )}

      {/* Versions panel */}
      {showVersions && data?.versions?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2" data-testid="memoria-versions">
          <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wide">Versiones previas (últimas {data.versions.length})</h4>
          <div className="space-y-1">
            {data.versions.map(v => (
              <div key={v.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-xs">
                <div>
                  <span className="font-semibold">{fmtDateTime(v.saved_at)}</span>
                  <span className="text-gray-500 ml-2">· {v.saved_by_name || v.saved_by || "—"}</span>
                  <span className="text-gray-400 ml-2">· {v.length} chars</span>
                </div>
                <button onClick={() => restoreVersion(v)} className="text-[#007934] hover:underline text-[11px] font-semibold">Cargar en editor →</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Split editor + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" style={{ minHeight: "540px" }}>
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Editor · Markdown</span>
            <span className="text-[10px] text-gray-400">{wordCount.toLocaleString("es-ES")} palabras · {lineCount} líneas</span>
          </div>
          <textarea
            value={markdown}
            onChange={e => { setMarkdown(e.target.value); setDirty(true); }}
            className="flex-1 p-4 text-xs font-mono resize-none focus:outline-none"
            style={{ minHeight: "500px" }}
            placeholder="Escribe o pega la memoria en Markdown. Usa # para encabezados, **negrita**, tablas con | y listas con -."
            data-testid="memoria-editor"
          />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" style={{ minHeight: "540px" }}>
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Vista previa</span>
          </div>
          <div className="p-5 prose prose-sm max-w-none overflow-y-auto" style={{ maxHeight: "540px" }}>
            {hasContent ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
            ) : (
              <p className="text-gray-400 text-sm">La vista previa aparecerá aquí mientras escribes o tras generar con Pando.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Chat Pando ────────────────────────────────────────────────────────────────────
function ChatPanel({ lic }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef();

  const QUICK = [
    "¿Cuáles son las principales necesidades IT del pliego?",
    "¿Qué aplicaciones VSM podemos reutilizar?",
    "¿Cuál es el coste estimado de implantación?",
    "¿Qué SLAs exige el contrato?",
    "¿Hay cláusulas de penalización relevantes?",
  ];

  useEffect(() => {
    axios.get(`${API}/licitaciones/${lic.id}/chat`).then(r => setMessages(r.data || [])).catch(() => {});
  }, [lic.id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;
    setInput("");
    setSending(true);
    const tempUser = { id: `tmp-u`, role: "user", content: msg, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUser]);
    try {
      const res = await axios.post(`${API}/licitaciones/${lic.id}/chat`, { message: msg });
      const aiMsg = { id: res.data.message_id, role: "assistant", content: res.data.response, created_at: new Date().toISOString() };
      setMessages(prev => [...prev.filter(m => m.id !== "tmp-u"), tempUser, aiMsg]);
    } catch (e) {
      setMessages(prev => prev.filter(m => m.id !== "tmp-u"));
    } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-full max-h-[600px]" data-testid="chat-panel">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-4">
            <Bot size={28} className="mx-auto mb-2 text-gray-200" />
            <p className="text-sm text-gray-500 mb-4">Pregunta a la Pando sobre este expediente</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {QUICK.map(q => (
                <button key={q} onClick={() => send(q)} className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-[#007934] hover:bg-[#E8F5E9] text-left transition-all">{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === "user" ? "bg-[#007934]" : "bg-gray-100"}`}>
              {m.role === "user" ? <User size={13} className="text-white" /> : <Bot size={13} className="text-gray-600" />}
            </div>
            <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[#007934] text-white rounded-tr-sm" : "bg-gray-100 text-gray-800 rounded-tl-sm"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex gap-2">
            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center"><Bot size={13} className="text-gray-600" /></div>
            <div className="bg-gray-100 px-3 py-2 rounded-2xl rounded-tl-sm"><Loader2 size={14} className="animate-spin text-gray-400" /></div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div className="p-3 border-t border-gray-100">
        <form onSubmit={e => { e.preventDefault(); send(); }} className="flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} placeholder="Pregunta sobre el pliego, necesidades IT, costes..."
            className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="chat-input" />
          <button type="submit" disabled={!input.trim() || sending} className="bg-[#007934] text-white p-2 rounded-xl hover:bg-[#006528] disabled:opacity-50" data-testid="chat-send-btn">
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Pestaña Innovación ─────────────────────────────────────────────────────────
function TabInnovacion({ lic }) {
  const plan = lic.plan_innovacion;
  const eco = lic.economico_final;
  const si  = lic.sistema_informatico;
  const valCfo = lic.validacion_cfo;
  const estadoCfo = lic.estado_validacion_cfo || valCfo?.estado || null;
  const estadoCfoSi = lic.estado_validacion_cfo_si || si?.validacion_cfo_si?.estado || null;

  if (!plan && !eco && !si) {
    return (
      <div className="p-8 text-center" data-testid="innovacion-empty">
        <Sparkles size={40} className="mx-auto mb-4 text-gray-200" />
        <h3 className="text-base font-bold text-gray-800 mb-2">Sin plan de innovación</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          Esta licitación aún no tiene plan de innovación cargado.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tab-innovacion-panel">
      {eco && <BloqueEconomicoCFO lic={lic} eco={eco} valCfo={valCfo} estadoCfo={estadoCfo} />}
      {si  && <BloqueSistemaInformaticoCFO lic={lic} si={si} estadoCfo={estadoCfoSi} />}
      {plan && <BloqueInnovacion plan={plan} lic={lic} />}
    </div>
  );
}

function BloqueEconomicoCFO({ lic, eco, valCfo, estadoCfo }) {
  const [acting, setActing] = useState(false);
  const { user } = useAuth();
  const fmt = (n) => `${new Intl.NumberFormat("es-ES").format(Math.round(n || 0))} €`;
  const fmtPct = (n) => `${((n || 0) * 100).toFixed(2)} %`;
  const isPriv = user?.role === "cfo" || user?.role === "admin";

  const accion = async (acc) => {
    if (!isPriv) return;
    setActing(true);
    try {
      const comentario = acc === "aprobar"
        ? "Datos económicos validados por CFO"
        : (acc === "rechazar" ? prompt("Motivo del rechazo:") || "" : "");
      const { data } = await axios.post(`${API}/licitaciones/${lic.id}/validacion-cfo`, {
        accion: acc,
        comentario,
      });
      toast.success(`Validación CFO: ${data.estado}`);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al validar");
    } finally { setActing(false); }
  };

  const estadoBadge = {
    pendiente:  { txt: "PENDIENTE VALIDACIÓN CFO", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    aprobado:   { txt: "APROBADO POR CFO",         cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    rechazado:  { txt: "RECHAZADO POR CFO",        cls: "bg-rose-100 text-rose-800 border-rose-300" },
  }[estadoCfo] || { txt: "SIN ESTADO", cls: "bg-gray-100 text-gray-700 border-gray-200" };

  const bc = eco.business_case || {};
  const m  = eco.mantenimiento || {};

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="bloque-economico-cfo">
      <div className={`flex items-center justify-between px-5 py-3 border-b ${estadoBadge.cls.replace("text-", "").split(" ")[0]} bg-opacity-30`}
           style={{ background: estadoCfo === "aprobado" ? "#ECFDF5" : estadoCfo === "rechazado" ? "#FFF1F2" : "#FFFBEB" }}>
        <div className="flex items-center gap-3">
          <Euro size={18} className="text-gray-700" />
          <h3 className="text-sm font-bold text-gray-900 font-['Outfit']">
            Datos económicos finales · {eco.cliente || "Ayuntamiento"}
          </h3>
          <span className={`text-[10px] font-bold uppercase tracking-wider border px-2 py-0.5 rounded ${estadoBadge.cls}`}
                data-testid="badge-cfo">
            {estadoBadge.txt}
          </span>
        </div>
        {isPriv && (
          <div className="flex gap-2">
            {estadoCfo !== "aprobado" && (
              <button onClick={() => accion("aprobar")} disabled={acting}
                className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                data-testid="cfo-aprobar">
                {acting ? <Loader2 size={12} className="animate-spin"/> : "Aprobar (CFO)"}
              </button>
            )}
            {estadoCfo !== "rechazado" && (
              <button onClick={() => accion("rechazar")} disabled={acting}
                className="text-xs border border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                data-testid="cfo-rechazar">
                Rechazar
              </button>
            )}
          </div>
        )}
      </div>

      <div className="p-5 space-y-4">
        {valCfo?.comentario && (
          <p className="text-xs text-gray-600 italic bg-gray-50 rounded p-2 border border-gray-100">
            {valCfo.comentario}
          </p>
        )}
        {valCfo?.aprobado_por && estadoCfo === "aprobado" && (
          <p className="text-xs text-emerald-700">
            Aprobado por <strong>{valCfo.aprobado_por}</strong> el {String(valCfo.aprobado_at || "").slice(0,10)}
          </p>
        )}

        {/* KPIs principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpis-eco">
          <KpiInline label="Importe contrato 4y IVA inc." value={fmt(eco.importe_total_contrato_con_iva)} color="emerald" testId="kpi-contrato"/>
          <KpiInline label="Mantenimiento anual sin IVA"  value={fmt(m.anual_sin_iva)}          color="teal"   testId="kpi-mant"/>
          <KpiInline label="CAPEX"                          value={fmt(bc.capex_total_eur)}     color="indigo" testId="kpi-capex-bc"/>
          <KpiInline label="EBITDA 4 años"                  value={fmt(bc.ebitda_total_4y)}     color="sky"    testId="kpi-ebitda"/>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiInline label="IRR Unlevered"                  value={fmtPct(bc.unlevered_irr)}    color="emerald" testId="kpi-irr" flagReview={typeof bc.unlevered_irr === "number" && Math.abs(bc.unlevered_irr) > 1}/>
          <KpiInline label="Money Multiple"                 value={`${(bc.money_multiple || 0).toFixed(2)}x`} color="teal" testId="kpi-mm"/>
          <KpiInline label="Payback"                         value={`${(bc.payback_anios || 0).toFixed(2)} años`} color="indigo" testId="kpi-payback" flagReview={typeof bc.payback_anios === "number" && (bc.payback_anios > 15 || bc.payback_anios < 0)}/>
          <KpiInline label="EBITDA margen medio"            value={fmtPct(bc.ebitda_margin_pct_medio)} color="sky" testId="kpi-ebitda-margin"/>
        </div>

        {/* EBITDA por año */}
        {bc.ebitda_anual && (
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-2 mt-2">EBITDA por año</p>
            <div className="grid grid-cols-4 gap-2 text-xs">
              {bc.ebitda_anual.map((y) => (
                <div key={y.anio} className="bg-gray-50 rounded p-2 border border-gray-100">
                  <p className="text-[10px] uppercase text-gray-500 font-semibold">Año {y.anio}</p>
                  <p className="font-bold text-gray-900">{fmt(y.valor)}</p>
                  <p className="text-[10px] text-gray-500">{fmtPct(y.margen_pct)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-gray-500 mt-2">
          Fuente: <code>{eco.fuente}</code> · Actualizado {String(eco.actualizado_at || "").slice(0, 10)}
        </p>
      </div>
    </div>
  );
}

function BloqueSistemaInformaticoCFO({ lic, si, estadoCfo }) {
  const [acting, setActing] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const { user } = useAuth();
  const fmt = (n) => `${new Intl.NumberFormat("es-ES").format(Math.round(n || 0))} €`;
  const isPriv = user?.role === "cfo" || user?.role === "admin";
  const valCfo = si?.validacion_cfo_si;

  const accion = async (acc) => {
    if (!isPriv) return;
    setActing(true);
    try {
      const comentario = acc === "aprobar"
        ? "CAPEX y OPEX de Sistema Informático aprobados por CFO"
        : (acc === "rechazar" ? prompt("Motivo del rechazo:") || "" : "");
      const { data } = await axios.post(`${API}/licitaciones/${lic.id}/validacion-cfo`, {
        accion: acc,
        scope: "sistema_informatico",
        comentario,
      });
      toast.success(`Validación SI: ${data.estado}`);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al validar");
    } finally { setActing(false); }
  };

  const estadoBadge = {
    pendiente:  { txt: "PENDIENTE VALIDACIÓN CFO · IT", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    aprobado:   { txt: "APROBADO POR CFO · IT",         cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    rechazado:  { txt: "RECHAZADO POR CFO · IT",        cls: "bg-rose-100 text-rose-800 border-rose-300" },
  }[estadoCfo] || { txt: "SIN ESTADO IT", cls: "bg-gray-100 text-gray-700 border-gray-200" };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="bloque-si-cfo">
      <div className="flex items-center justify-between px-5 py-3 border-b"
           style={{ background: estadoCfo === "aprobado" ? "#ECFDF5" : estadoCfo === "rechazado" ? "#FFF1F2" : "#FFFBEB" }}>
        <div className="flex items-center gap-3 flex-wrap">
          <Layers size={18} className="text-indigo-700" />
          <h3 className="text-sm font-bold text-gray-900 font-['Outfit']">
            Sistema Informático · {si.contrato || lic.titulo}
          </h3>
          {si.proveedor && (
            <span className="text-[10px] bg-gray-900 text-white font-mono px-2 py-0.5 rounded">
              {si.proveedor}
            </span>
          )}
          <span className={`text-[10px] font-bold uppercase tracking-wider border px-2 py-0.5 rounded ${estadoBadge.cls}`}
                data-testid="badge-cfo-si">
            {estadoBadge.txt}
          </span>
        </div>
        {isPriv && (
          <div className="flex gap-2">
            {estadoCfo !== "aprobado" && (
              <button onClick={() => accion("aprobar")} disabled={acting}
                className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                data-testid="cfo-si-aprobar">
                {acting ? <Loader2 size={12} className="animate-spin"/> : "Aprobar SI (CFO)"}
              </button>
            )}
            {estadoCfo !== "rechazado" && (
              <button onClick={() => accion("rechazar")} disabled={acting}
                className="text-xs border border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                data-testid="cfo-si-rechazar">
                Rechazar SI
              </button>
            )}
          </div>
        )}
      </div>

      <div className="p-5 space-y-4">
        {valCfo?.comentario && (
          <p className="text-xs text-gray-600 italic bg-gray-50 rounded p-2 border border-gray-100">
            {valCfo.comentario}
          </p>
        )}
        {valCfo?.aprobado_por && estadoCfo === "aprobado" && (
          <p className="text-xs text-emerald-700">
            Aprobado por <strong>{valCfo.aprobado_por}</strong> el {String(valCfo.aprobado_at || "").slice(0,10)}
          </p>
        )}

        {/* KPIs principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpis-si">
          <KpiInline label="CAPEX SI"           value={fmt(si.capex_eur)}                 color="indigo" testId="kpi-si-capex"/>
          <KpiInline label="OPEX SI / año"      value={fmt(si.opex_anual_eur)}            color="teal"   testId="kpi-si-opex"/>
          <KpiInline label="Disponibilidad/año" value={fmt(si.disponibilidad_anual_eur)}  color="sky"    testId="kpi-si-disp"/>
          <KpiInline label="Total / año"        value={fmt(si.total_anual_eur)}           color="emerald" testId="kpi-si-total"/>
        </div>
        <p className="text-[11px] text-gray-500">
          Amortización a {si.amortizacion_anios || 8} años · Tasa financiación {((si.tasa_financiacion || 0)*100).toFixed(1)}%
          {" · "}Amort. anual {fmt(si.amortizacion_anual_eur)} + Financ. anual {fmt(si.financiacion_anual_eur)}
        </p>

        {/* Toggle desglose */}
        <button
          onClick={() => setShowDetail(!showDetail)}
          className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
          data-testid="si-toggle-detail">
          {showDetail ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
          {showDetail ? "Ocultar desglose CAPEX/OPEX" : "Ver desglose CAPEX/OPEX"}
        </button>

        {showDetail && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
            {si.capex_breakdown?.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-900">
                  CAPEX · {fmt(si.capex_eur)} ({si.capex_breakdown.length} bloques)
                </div>
                <table className="w-full text-xs" data-testid="si-capex-table">
                  <tbody>
                    {si.capex_breakdown.map((b, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-3 py-1.5 text-gray-700">{b.bloque}</td>
                        <td className="px-2 py-1.5 text-right text-gray-500 tabular-nums w-16">{b.ud} ud</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900 w-28">{fmt(b.subtotal_eur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {si.opex_breakdown?.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-teal-50 px-3 py-2 text-xs font-bold text-teal-900">
                  OPEX anual · {fmt(si.opex_anual_eur)} ({si.opex_breakdown.length} conceptos)
                </div>
                <table className="w-full text-xs" data-testid="si-opex-table">
                  <tbody>
                    {si.opex_breakdown.map((b, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-3 py-1.5 text-gray-700">{b.concepto}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900 w-28">{fmt(b.anual_eur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <p className="text-[11px] text-gray-500 mt-2">
          Fuente: <code>{si.fuente}</code>{si.actualizado_at && ` · Actualizado ${String(si.actualizado_at).slice(0, 10)}`}
        </p>
      </div>
    </div>
  );
}

function BloqueInnovacion({ plan, lic }) {
  const fmt = (n) => `${new Intl.NumberFormat("es-ES").format(n || 0)} €`;
  const tot = plan.totales_medidas || {};
  const medidas = plan.medidas_principales || [];
  const dominios = plan.dominios || [];

  return (
    <>
      <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 border border-emerald-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <TreePine size={18} className="text-emerald-700" />
          <h3 className="text-base font-bold text-emerald-900 font-['Outfit']">
            Plan de Innovación · {plan.contrato || lic.titulo}
          </h3>
          <span className="text-xs text-emerald-700 ml-auto">Horizonte {plan.horizonte_anios || 4} años</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiInline label="CAPEX inicial"      value={fmt(tot.capex_eur)}         color="emerald" testId="kpi-capex"/>
          <KpiInline label="OPEX medidas/año"   value={fmt(tot.opex_anual_eur)}    color="teal"    testId="kpi-opex-medidas"/>
          <KpiInline label="OPEX plan total/año" value={fmt(plan.total_opex_anual_eur)} color="sky" testId="kpi-opex-plan"/>
          <KpiInline label="Total 4 años"        value={fmt(tot.total_4_anios_eur)} color="indigo"  testId="kpi-total-4y"/>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-bold text-gray-800 mb-3 font-['Outfit']">
          {medidas.length} medidas tecnológicas comerciales
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="medidas-grid">
          {medidas.map((m) => (
            <div key={m.id}
                 className="bg-white border border-gray-200 rounded-lg p-4 hover:border-emerald-400 hover:shadow-md transition"
                 data-testid={`medida-${m.id}`}>
              <div className="flex items-start gap-2 mb-2">
                <Sparkles size={14} className="text-emerald-600 mt-1 flex-shrink-0" />
                <p className="text-sm font-bold text-gray-900">{m.nombre}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div>
                  <p className="text-gray-500 uppercase tracking-wider text-[10px]">CAPEX</p>
                  <p className="font-bold text-gray-900">{fmt(m.capex_eur)}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wider text-[10px]">OPEX/año</p>
                  <p className="font-bold text-gray-900">{fmt(m.opex_anual_eur)}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wider text-[10px]">4 años</p>
                  <p className="font-bold text-emerald-700">{fmt(m.total_4_anios_eur)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {dominios.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-gray-800 mb-3 font-['Outfit']">
            Desglose por dominio · OPEX anual
          </h4>
          <div className="space-y-3" data-testid="dominios-list">
            {dominios.map((d) => (
              <div key={d.codigo} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b">
                  <p className="text-sm font-bold text-gray-800">
                    <span className="inline-block w-7 h-7 mr-2 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold leading-7 text-center">{d.codigo}</span>
                    {d.nombre}
                  </p>
                  <p className="text-sm font-bold text-emerald-700 tabular-nums" data-testid={`subtotal-${d.codigo}`}>
                    {fmt(d.subtotal_opex_eur)}/año
                  </p>
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    {(d.actuaciones || []).map((a, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-4 py-1.5 text-gray-700">{a.nombre}</td>
                        <td className="px-4 py-1.5 text-right tabular-nums text-gray-600 w-32">{fmt(a.opex_eur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      {plan.fuentes && plan.fuentes.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Fuentes</p>
          <ul className="text-xs text-gray-700 space-y-0.5">
            {plan.fuentes.map((f, i) => <li key={i}>· {f}</li>)}
          </ul>
        </div>
      )}
    </>
  );
}

function KpiInline({ label, value, color = "emerald", testId, flagReview = false }) {
  const bg = {
    emerald: "bg-emerald-100 text-emerald-900",
    teal:    "bg-teal-100 text-teal-900",
    sky:     "bg-sky-100 text-sky-900",
    indigo:  "bg-indigo-100 text-indigo-900",
  }[color] || "bg-gray-100 text-gray-900";
  // Iter304 · D08/E07 — valor fuera del rango esperado para este negocio:
  // se muestra tal cual (no se inventa ni se recorta) pero con aviso visual
  // de que necesita revisión antes de usarse en una decisión.
  return (
    <div className={`${flagReview ? "bg-purple-100 text-purple-900 ring-1 ring-purple-300" : bg} rounded-lg p-3`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider opacity-70 font-semibold flex items-center gap-1">
        {label}
        {flagReview && <span title="Fuera del rango esperado — revisar origen del dato antes de usarlo" data-testid={`${testId}-flag`}>🔎</span>}
      </p>
      <p className="text-lg font-bold font-['Outfit'] mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB: RESUMEN EJECUTIVO  (Iter235 · feedback delegados)
//   KPIs grandes del contrato · mapa + mercado cercano · composición de la
//   oferta · curva económica con escenarios · adjudicador actual ·
//   inteligencia de mercado/CRM.
//   KpiBox/ComposicionOferta/CurvaEconomica viven en
//   components/exec_summary/ (compartidos con Delegado.jsx, Iter243).
// ════════════════════════════════════════════════════════════════════════════
function TabResumenEjecutivo({ lic }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/licitaciones/${lic.id}/exec-summary`);
      setData(r.data);
    } catch { toast.error("No se pudo cargar el resumen ejecutivo"); }
    setLoading(false);
  }, [lic.id]);

  useEffect(() => { load(); }, [load]);

  const extract = async () => {
    setExtracting(true);
    try {
      await axios.post(`${API}/licitaciones/${lic.id}/exec-summary/extract`);
      toast.success("KPIs extraídos del PCAP/PPT");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error extrayendo KPIs"); }
    setExtracting(false);
  };

  const lookupAdjudicador = async () => {
    setLookingUp(true);
    try {
      await axios.post(`${API}/licitaciones/${lic.id}/exec-summary/lookup-adjudicador`);
      toast.success("Búsqueda online completada");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error en la búsqueda online"); }
    setLookingUp(false);
  };

  if (loading) return <div className="p-8 text-center text-xs text-gray-400" data-testid="resumen-loading"><Loader2 size={18} className="animate-spin mx-auto mb-2" />Cargando resumen ejecutivo…</div>;
  const kpi = data?.kpi_extraction;

  return (
    <div className="p-5 space-y-5" data-testid="tab-resumen-ejecutivo-content">
      {/* KPIs grandes */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">KPIs del contrato</h3>
          <button onClick={extract} disabled={extracting} data-testid="resumen-extract-btn"
            className="text-xs text-[#007934] font-semibold flex items-center gap-1 disabled:opacity-50">
            {extracting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {kpi ? "Re-extraer con IA" : "Extraer con IA del PCAP/PPT"}
          </button>
        </div>
        {!kpi && !extracting && (
          <p className="text-xs text-gray-400 mb-2">Sin extraer todavía. Pulsa "Extraer con IA" para leer el PCAP/PPT subido y rellenar duración, prórroga, lotes, inversión y criterios.</p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
          <KpiBox label="Duración" icon={Clock} value={kpi?.duracion_anios ? `${kpi.duracion_anios} años` : "—"} />
          <KpiBox label="Prórroga" icon={Calendar} value={kpi?.prorroga_meses ? `${kpi.prorroga_meses} meses` : (kpi?.prorroga_texto ? "Sí" : "—")} hint={kpi?.prorroga_texto} />
          <KpiBox label="Lotes" icon={Layers} value={kpi?.lotes_count ?? "—"} hint={(kpi?.lotes_detalle || []).join(" · ")} />
          <KpiBox label="CAPEX pliego" icon={Euro} value={kpi?.capex_pliego_eur ? fmtEur(kpi.capex_pliego_eur) : "—"} hint={kpi?.inversion_detalle} />
          <KpiBox label="OPEX pliego/año" icon={TrendingUp} value={kpi?.opex_pliego_eur_anual ? fmtEur(kpi.opex_pliego_eur_anual) : "—"} />
        </div>
        {kpi?.inversion_detalle && !kpi?.capex_pliego_eur && (
          <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 mt-2">{kpi.inversion_detalle}</p>
        )}
        {kpi?.extracted_at && <p className="text-[10px] text-gray-400 mt-2">Extraído el {fmtDateTime(kpi.extracted_at)} de {(kpi.source_docs || []).join(", ") || "—"} · confianza {kpi.confianza || "—"}</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Mapa + mercado cercano */}
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Target size={12} />Ubicación y mercado cercano</h3>
          {data?.geo ? (
            <iframe
              title="mapa-municipio" data-testid="resumen-mapa"
              className="w-full h-40 rounded-lg border border-gray-200"
              src={`https://maps.google.com/maps?q=${data.geo.lat},${data.geo.lng}&z=11&output=embed`}
              loading="lazy"
            />
          ) : <div className="h-40 rounded-lg bg-gray-50 flex items-center justify-center text-xs text-gray-400">Sin ubicación geocodificada</div>}
          {data?.nearby_market && (
            <p className="text-[11px] text-gray-600 mt-2" data-testid="resumen-nearby-market">
              Mercado en <b>{data.nearby_market.provincia}</b>: <b>{data.nearby_market.n_licitaciones}</b> licitaciones por {fmtEur(data.nearby_market.importe_total)} en total.
            </p>
          )}
        </div>

        {/* Adjudicador actual */}
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Trophy size={12} />Adjudicador actual</h3>
          {data?.adjudicador_actual ? (
            <div data-testid="resumen-adjudicador">
              <p className="text-sm font-semibold text-gray-800">{data.adjudicador_actual}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Fuente: {data.fuente_adjudicador}</p>
            </div>
          ) : (
            <p className="text-xs text-gray-400 mb-2">Sin dato interno ni mención en el pliego.</p>
          )}
          <button onClick={lookupAdjudicador} disabled={lookingUp} data-testid="resumen-lookup-adjudicador-btn"
            className="text-xs text-[#007934] font-semibold flex items-center gap-1 mt-2 disabled:opacity-50">
            {lookingUp ? <Loader2 size={12} className="animate-spin" /> : <FileSearch size={12} />}
            Buscar online (verificar)
          </button>
        </div>
      </div>

      {/* Composición de la oferta */}
      <div className="bg-white border border-gray-200 rounded-xl p-3">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><BarChart2 size={12} />Composición de la oferta</h3>
        <ComposicionOferta kpi={kpi} />
      </div>

      {/* Curva económica */}
      <div className="bg-white border border-gray-200 rounded-xl p-3">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><TrendingUp size={12} />Curva económica — simulador de escenarios</h3>
        <CurvaEconomica
          kpi={kpi}
          curve={data?.economic_curve}
          simulateFn={async (v) => (await axios.post(`${API}/licitaciones/${lic.id}/exec-summary/simulate-economica`, { baja_pct: v })).data}
        />
      </div>

      {/* Inteligencia de mercado / CRM */}
      {data?.client_intel && (
        <div className="bg-white border border-gray-200 rounded-xl p-3" data-testid="resumen-client-intel">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5"><Brain size={12} />Inteligencia de mercado — {data.client_intel.cliente}</h3>
            <a href="/tender-intelligence" target="_blank" rel="noreferrer" className="text-[10px] text-[#007934] font-semibold flex items-center gap-0.5">Ver completo <ExternalLink size={10} /></a>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <KpiBox label="Ofertas" value={data.client_intel.summary?.ofertas_count ?? 0} />
            <KpiBox label="Ganadas VSM" value={data.client_intel.summary?.ganadas_vsm ?? 0} />
            <KpiBox label="Win rate" value={data.client_intel.summary?.win_rate_pct != null ? `${data.client_intel.summary.win_rate_pct}%` : "n/d"} />
          </div>
          {data.client_intel.competitors_stats?.length > 0 && (
            <div className="text-[11px] text-gray-600">
              <b>Competidores:</b> {data.client_intel.competitors_stats.map(c => c.name).join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Detail Page ───────────────────────────────────────────────────────────
const TABS = [
  { id: "resumen",    label: "Resumen Ejecutivo", icon: Target      },
  { id: "documentos", label: "Documentos",    icon: FileText    },
  { id: "analisis",   label: "Análisis Pando",   icon: Brain       },
  { id: "cobertura",  label: "Cobertura VSM", icon: BarChart2   },
  { id: "innovacion", label: "Innovación",    icon: Sparkles    },
  { id: "memoria",    label: "Memoria",       icon: BookOpen    },
  { id: "historico",  label: "Histórico municipio", icon: Archive },
  { id: "chat",       label: "Chat Pando",       icon: MessageSquare },
];

// ════════════════════════════════════════════════════════════════════════════
// TAB: HISTÓRICO DEL MUNICIPIO  (insights agregados + tabla)
// ════════════════════════════════════════════════════════════════════════════
function HistoricoBadge({ municipio, onJump }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!municipio) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/licitaciones-import/historico/insights/${encodeURIComponent(municipio)}`);
        if (alive) setData(r.data);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [municipio]);
  if (!data) return null;
  return (
    <button
      type="button"
      onClick={onJump}
      data-testid="historico-badge"
      title="Ver pestaña Histórico del municipio"
      className="w-full text-left bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 hover:border-violet-400 rounded-xl px-3 py-2 flex items-center gap-3 transition-all hover:shadow-sm group"
    >
      <Brain size={14} className="text-violet-600 shrink-0"/>
      <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap text-[11px]">
        <span className="font-semibold text-violet-900">Histórico {data.municipio}:</span>
        <span className="text-gray-700"><b>{data.total}</b> licit.</span>
        <span className="text-gray-400">·</span>
        <span className={
          data.win_rate >= 30 ? "text-emerald-700 font-semibold" :
          data.win_rate >= 15 ? "text-amber-700 font-semibold" :
          "text-rose-700 font-semibold"
        }>{data.win_rate}% win</span>
        {data.ganador_tipico && (<>
          <span className="text-gray-400">·</span>
          <span className="text-gray-700 inline-flex items-center gap-1">
            <TrophyIcon size={10} className="text-amber-500"/>
            <b>{data.ganador_tipico}</b>
          </span>
        </>)}
        {data.importe_medio && (<>
          <span className="text-gray-400">·</span>
          <span className="text-gray-700">~{(data.importe_medio/1e6).toFixed(1)}M€ medio</span>
        </>)}
      </div>
      <ChevronRight size={12} className="text-violet-400 group-hover:text-violet-700 transition-colors"/>
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB: HISTÓRICO DEL MUNICIPIO  (insights agregados + tabla)
// ════════════════════════════════════════════════════════════════════════════
function TabHistoricoMunicipio({ lic }) {
  const [insights, setInsights] = useState(null);
  const [licitaciones, setLicitaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!lic?.municipio) { setLoading(false); return; }
      setLoading(true);
      setNotFound(false);
      try {
        const muni = encodeURIComponent(lic.municipio);
        const [r1, r2] = await Promise.allSettled([
          axios.get(`${API}/licitaciones-import/historico/insights/${muni}`),
          axios.get(`${API}/licitaciones-import/historico/licitaciones/${muni}?limit=200`),
        ]);
        if (!alive) return;
        if (r1.status === "fulfilled") setInsights(r1.value.data);
        else if (r1.reason?.response?.status === 404) setNotFound(true);
        if (r2.status === "fulfilled") setLicitaciones(r2.value.data.items || []);
      } catch {}
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [lic?.municipio]);

  const triggerLearn = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/licitaciones-import/learn`);
      toast.success("Índice regenerado");
      // refetch
      const muni = encodeURIComponent(lic.municipio);
      const r = await axios.get(`${API}/licitaciones-import/historico/insights/${muni}`);
      setInsights(r.data);
      setNotFound(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error indexando");
    } finally { setLoading(false); }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-400 text-xs"><Loader2 className="inline animate-spin mr-2" size={14}/>Cargando histórico…</div>;
  }

  if (notFound && licitaciones.length === 0) {
    return (
      <div className="p-8 text-center" data-testid="historico-empty">
        <Archive size={36} className="mx-auto text-gray-300 mb-3"/>
        <p className="text-sm text-gray-700 font-semibold">Aún no hay histórico indexado para <span className="text-[#007934]">{lic?.municipio}</span></p>
        <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
          Importa un Excel histórico desde el listado de Licitaciones y luego pulsa
          &ldquo;Aprender de mi histórico&rdquo;, o pulsa el botón inferior para reconstruir el índice global.
        </p>
        <button
          onClick={triggerLearn}
          data-testid="historico-rebuild"
          className="mt-4 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5">
          <Brain size={12}/> Construir índice ahora
        </button>
      </div>
    );
  }

  // Caso intermedio: hay licitaciones en BD pero no hay índice → CTA inline + tabla
  const showMissingIndexBanner = notFound && licitaciones.length > 0;

  return (
    <div className="p-5 space-y-4" data-testid="historico-tab">
      {showMissingIndexBanner && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-center gap-3"
          data-testid="historico-missing-index-banner"
        >
          <Brain size={16} className="text-amber-600 shrink-0"/>
          <div className="flex-1 text-[11px] text-amber-900">
            <p className="font-semibold">Hay {licitaciones.length} licitaciones de {lic?.municipio} en el CRM, pero el índice agregado aún no se ha construido.</p>
            <p className="text-amber-700">Pulsa para calcular ganador típico, importe medio, competidores recurrentes y winsight de chat Pando.</p>
          </div>
          <button
            onClick={triggerLearn}
            data-testid="historico-rebuild"
            className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shrink-0 inline-flex items-center gap-1.5">
            <Brain size={12}/> Construir índice ahora
          </button>
        </div>
      )}
      {insights && (
        <div className="bg-gradient-to-r from-violet-50 via-indigo-50 to-violet-50 border border-violet-200 rounded-xl p-4" data-testid="historico-insights-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-violet-900 flex items-center gap-2">
              <Brain size={14}/> Insights de {insights.municipio}
            </h3>
            <button
              onClick={triggerLearn}
              data-testid="historico-rebuild"
              className="text-[10px] text-violet-700 hover:underline inline-flex items-center gap-1">
              <RefreshCw size={10}/> Re-indexar
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
            <Mini label="Total" value={insights.total} sub={`${insights.ganadas}G · ${insights.perdidas}P`}/>
            <Mini label="Win-rate" value={`${insights.win_rate}%`} cls={insights.win_rate >= 30 ? "text-emerald-700" : insights.win_rate >= 15 ? "text-amber-700" : "text-rose-700"}/>
            <Mini label="Importe medio" value={insights.importe_medio ? new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(insights.importe_medio) : "—"}/>
            <Mini label="Ganador típico" value={insights.ganador_tipico || "—"} icon={<TrophyIcon size={11} className="text-amber-500"/>}/>
          </div>
          {(insights.competidores_rank?.length > 0 || insights.tipos_servicio_rank?.length > 0) && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {insights.competidores_rank?.length > 0 && (
                <div className="bg-white rounded-lg p-2.5 border border-violet-100">
                  <p className="text-[9px] uppercase font-bold text-violet-500 mb-1.5 tracking-wide">Competidores recurrentes</p>
                  <div className="space-y-1">
                    {insights.competidores_rank.slice(0,5).map((c, i) => (
                      <div key={`comp-${i}-${c?.name || "?"}`} className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-700">{i+1}. {c.name}</span>
                        <span className="text-violet-700 font-mono font-bold">{c.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {insights.tipos_servicio_rank?.length > 0 && (
                <div className="bg-white rounded-lg p-2.5 border border-violet-100">
                  <p className="text-[9px] uppercase font-bold text-violet-500 mb-1.5 tracking-wide">Tipos de servicio</p>
                  <div className="space-y-1">
                    {insights.tipos_servicio_rank.slice(0,5).map((t, i) => (
                      <div key={`tipo-${i}-${t?.name || "?"}`} className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-700">{i+1}. {t.name}</span>
                        <span className="text-violet-700 font-mono font-bold">{t.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <p className="text-[10px] text-violet-600 mt-3 italic">
            Estos insights se inyectan automáticamente en el chat Pando de esta licitación.
          </p>
        </div>
      )}

      {licitaciones.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold text-gray-700 flex items-center justify-between">
            <span>Licitaciones históricas ({licitaciones.length})</span>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="text-[11px] w-full">
              <thead className="bg-gray-50 text-gray-500 uppercase text-[9px] sticky top-0">
                <tr>
                  <th className="text-left p-2">Expediente</th>
                  <th className="text-left p-2">Título</th>
                  <th className="text-left p-2">Servicio</th>
                  <th className="text-left p-2">Estado</th>
                  <th className="text-right p-2">Importe</th>
                  <th className="text-left p-2">Ganador</th>
                  <th className="text-left p-2">F. Present.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-testid="historico-table">
                {licitaciones.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="p-2 font-mono text-gray-600">{l.expediente || "—"}</td>
                    <td className="p-2 text-gray-800 max-w-[260px] truncate" title={l.titulo}>{l.titulo}</td>
                    <td className="p-2 text-gray-600">{l.tipo_servicio || "—"}</td>
                    <td className="p-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                        l.estado_crm === "ganada" ? "bg-emerald-100 text-emerald-700" :
                        l.estado_crm === "perdida" ? "bg-rose-100 text-rose-700" :
                        l.estado_crm === "presentada" ? "bg-blue-100 text-blue-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>
                        {l.estado_crm || "—"}
                      </span>
                    </td>
                    <td className="p-2 text-right font-mono">
                      {l.importe_licitacion ? new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(l.importe_licitacion) : "—"}
                    </td>
                    <td className="p-2 text-gray-700 truncate max-w-[120px]" title={l.ganador}>{l.ganador || "—"}</td>
                    <td className="p-2 text-gray-500 font-mono">{fmtDate(l.fecha_presentacion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, sub, cls = "", icon = null }) {
  return (
    <div className="bg-white rounded-lg p-2.5 border border-violet-100">
      <div className="text-[9px] uppercase text-violet-500 font-bold tracking-wide">{label}</div>
      <div className={`text-base font-bold flex items-center gap-1 ${cls || "text-gray-900"}`}>
        {icon}<span className="truncate">{value}</span>
      </div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}


export default function LicitacionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lic, setLic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("resumen");
  const [analyzing, setAnalyzing] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldVal, setFieldVal] = useState("");
  const [savingField, setSavingField] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [actForm, setActForm] = useState({ tipo: "nota", descripcion: "" });
  const [savingAct, setSavingAct] = useState(false);

  const fetchLic = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/licitaciones/${id}`);
      setLic(res.data);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchLic(); }, [fetchLic]);

  const handleFileUploaded = () => fetchLic();
  const handleFileDeleted = async (fid) => {
    await axios.delete(`${API}/licitaciones/${id}/files/${fid}`);
    fetchLic();
  };

  const quickSave = async (field, val) => {
    setSavingField(true);
    try { await axios.put(`${API}/licitaciones/${id}`, { [field]: val }); await fetchLic(); }
    finally { setSavingField(false); setEditingField(null); }
  };

  const addActivity = async () => {
    if (!actForm.descripcion.trim()) return;
    setSavingAct(true);
    try { await axios.post(`${API}/licitaciones/${id}/activity`, actForm); setActForm({ tipo: "nota", descripcion: "" }); setShowActivity(false); await fetchLic(); }
    finally { setSavingAct(false); }
  };

  const deleteActivity = async (actId) => {
    await axios.delete(`${API}/licitaciones/${id}/activity/${actId}`);
    fetchLic();
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#007934]" size={28} /></div>;
  if (!lic) return <div className="text-center py-20 text-gray-500">Expediente no encontrado</div>;

  const daysLeft = lic.fecha_presentacion ? Math.ceil((new Date(lic.fecha_presentacion) - new Date()) / 86400000) : null;
  const estadoCfg = ESTADOS.find(e => e.id === lic.estado_crm) || ESTADOS[0];

  const InlineEdit = ({ field, displayValue, editValue, type = "text" }) => {
    // Use editValue for editing initial value, fall back to displayValue
    const startVal = editValue !== undefined ? editValue : displayValue;
    if (editingField === field) return (
      <div className="flex items-center gap-1 flex-wrap">
        {type === "select_estado" ? (
          <select value={fieldVal} onChange={e => setFieldVal(e.target.value)} autoFocus
            className="text-xs border border-[#007934] rounded px-2 py-1 focus:outline-none max-w-full">
            {ESTADOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
          </select>
        ) : type === "select_tipo" ? (
          <select value={fieldVal} onChange={e => setFieldVal(e.target.value)} autoFocus
            className="text-xs border border-[#007934] rounded px-2 py-1 focus:outline-none max-w-full">
            {TIPOS_SERVICIO.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        ) : type === "number" ? (
          <input type="number" value={fieldVal} onChange={e => setFieldVal(e.target.value)} autoFocus
            className="text-xs border border-[#007934] rounded px-2 py-1 w-28 focus:outline-none" />
        ) : type === "date" ? (
          <input type="date" value={fieldVal} onChange={e => setFieldVal(e.target.value)} autoFocus
            className="text-xs border border-[#007934] rounded px-2 py-1 focus:outline-none" />
        ) : (
          <input type="text" value={fieldVal} onChange={e => setFieldVal(e.target.value)} autoFocus
            className="text-xs border border-[#007934] rounded px-2 py-1 w-32 focus:outline-none" />
        )}
        <button onClick={() => quickSave(field, type === "number" ? Number(fieldVal) : fieldVal)}
          className="text-[#007934] hover:text-[#005a27]"><Check size={12} /></button>
        <button onClick={() => setEditingField(null)} className="text-gray-400 hover:text-gray-600"><X size={12} /></button>
      </div>
    );
    return (
      <button onClick={() => { setEditingField(field); setFieldVal(startVal != null ? String(startVal) : ""); }}
        className="flex items-center gap-1 text-gray-800 hover:text-[#007934] group text-left">
        <span className="text-sm font-medium">
          {displayValue || <span className="text-gray-400 italic">—</span>}
        </span>
        <Edit2 size={10} className="text-gray-300 group-hover:text-[#007934] opacity-0 group-hover:opacity-100 flex-shrink-0" />
      </button>
    );
  };

  return (
    <div className="space-y-4 w-full" data-testid="licitacion-detail-page">
      {/* Back + title */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate("/licitaciones")} className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-gray-400">{lic.expediente || "Sin expediente"}</span>
            <span className="text-gray-300">·</span>
            <InlineEdit field="estado_crm" displayValue={estadoCfg.label} editValue={lic.estado_crm} type="select_estado" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 font-['Outfit']">{lic.titulo}</h1>
        </div>
      </div>

      {/* Mini insights card del municipio (visible en todas las tabs) */}
      <HistoricoBadge municipio={lic.municipio} onJump={() => setTab("historico")} />

      {/* Main layout: Sidebar + Content */}
      <div className="flex gap-5">
        {/* Left sidebar: CRM data */}
        <div className="w-64 flex-shrink-0 space-y-4">
          {/* CRM card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">CRM</h3>
            {[
              { label: "Municipio",    field: "municipio",             displayValue: lic.municipio,           editValue: lic.municipio          },
              { label: "Tipo servicio",field: "tipo_servicio",          displayValue: lic.tipo_servicio,       editValue: lic.tipo_servicio,       type: "select_tipo" },
              { label: "Responsable",  field: "responsable_comercial", displayValue: lic.responsable_comercial, editValue: lic.responsable_comercial },
              { label: "Importe",      field: "importe_licitacion",    displayValue: lic.importe_licitacion ? `${Number(lic.importe_licitacion).toLocaleString("es-ES")}€` : null, editValue: lic.importe_licitacion || "", type: "number" },
              { label: "Presentación", field: "fecha_presentacion",    displayValue: fmtDate(lic.fecha_presentacion), editValue: lic.fecha_presentacion ? lic.fecha_presentacion.slice(0,10) : "", type: "date" },
            ].map(({label, field, displayValue, editValue, type}) => (
              <div key={field}>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
                <InlineEdit field={field} displayValue={displayValue} editValue={editValue} type={type} />
              </div>
            ))}
            {/* Probability */}
            <div>
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Prob. éxito</p>
                <span className="text-xs font-bold text-[#007934]">{lic.probabilidad_exito}%</span>
              </div>
              <input type="range" min={0} max={100} step={5} value={lic.probabilidad_exito || 50}
                onChange={e => quickSave("probabilidad_exito", Number(e.target.value))}
                className="w-full mt-1" />
            </div>
          </div>

          {/* Contacto */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Contacto Ayuntamiento</h3>
            {[
              { label: "Nombre",   field: "contacto_ayuntamiento.nombre",   val: lic.contacto_ayuntamiento?.nombre   },
              { label: "Cargo",    field: "contacto_ayuntamiento.cargo",    val: lic.contacto_ayuntamiento?.cargo    },
              { label: "Email",    field: "contacto_ayuntamiento.email",    val: lic.contacto_ayuntamiento?.email,   type: "email" },
              { label: "Teléfono", field: "contacto_ayuntamiento.telefono", val: lic.contacto_ayuntamiento?.telefono, type: "tel" },
            ].map(({label, field, val, type}) => (
              <div key={field}>
                <p className="text-[10px] text-gray-400">{label}</p>
                <InlineEdit field={field} displayValue={val} editValue={val} type={type || "text"} />
              </div>
            ))}
          </div>

          {/* Días para presentación */}
          {daysLeft !== null && (
            <div className={`rounded-xl p-3 text-center ${daysLeft < 0 ? "bg-red-50 border border-red-200" : daysLeft < 14 ? "bg-amber-50 border border-amber-200" : "bg-green-50 border border-green-200"}`}>
              <p className={`text-2xl font-bold ${daysLeft < 0 ? "text-red-600" : daysLeft < 14 ? "text-amber-600" : "text-[#007934]"}`}>{Math.abs(daysLeft)}</p>
              <p className="text-xs text-gray-600">{daysLeft < 0 ? "días expirado" : "días para presentar"}</p>
            </div>
          )}

          {/* Actividad CRM */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Actividad CRM</h3>
              <button onClick={() => setShowActivity(!showActivity)} className="text-xs text-[#007934] flex items-center gap-1"><Plus size={11} />Añadir</button>
            </div>
            {showActivity && (
              <div className="mb-3 space-y-2">
                <select value={actForm.tipo} onChange={e => setActForm(p => ({...p, tipo: e.target.value}))}
                  className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-[#007934]">
                  {ACTIVITY_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                <textarea rows={2} value={actForm.descripcion} onChange={e => setActForm(p => ({...p, descripcion: e.target.value}))}
                  placeholder="Descripción de la actividad..." className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-[#007934] resize-none" />
                <button onClick={addActivity} disabled={savingAct || !actForm.descripcion.trim()}
                  className="w-full bg-[#007934] text-white rounded py-1.5 text-xs font-medium disabled:opacity-50" data-testid="save-activity-btn">
                  {savingAct ? "Guardando…" : "Guardar"}
                </button>
              </div>
            )}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(lic.historial_actividad || []).slice().reverse().map(act => {
                const TypeCfg = ACTIVITY_TYPES.find(t => t.id === act.tipo) || ACTIVITY_TYPES[3];
                return (
                  <div key={act.id} className="flex items-start gap-2 group">
                    <TypeCfg.icon size={11} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700">{act.descripcion}</p>
                      <p className="text-[10px] text-gray-400">{fmtDate(act.fecha)} · {act.usuario}</p>
                    </div>
                    <button onClick={() => deleteActivity(act.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500"><X size={10} /></button>
                  </div>
                );
              })}
              {!(lic.historial_actividad || []).length && <p className="text-xs text-gray-400 text-center">Sin actividad registrada</p>}
            </div>
          </div>
        </div>

        {/* Right: main workspace */}
        <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Tabs */}
          <div className="flex gap-0 border-b border-gray-100 overflow-x-auto">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium transition-all border-b-2 flex-shrink-0 ${tab === t.id ? "border-[#007934] text-[#007934] bg-[#E8F5E9]/50" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                data-testid={`tab-${t.id}`}>
                <t.icon size={13} />{t.label}
                {t.id === "documentos" && (lic.archivos||[]).length > 0 && <span className="bg-[#007934] text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">{(lic.archivos||[]).length}</span>}
                {t.id === "analisis" && lic.analisis_ia && <span className="w-2 h-2 rounded-full bg-violet-400" />}
                {t.id === "memoria" && lic.memoria_tecnica && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === "resumen"    && <TabResumenEjecutivo lic={lic} />}
          {tab === "documentos" && <TabDocumentos lic={lic} onUploaded={handleFileUploaded} onDeleted={handleFileDeleted} />}
          {tab === "analisis"   && <TabAnalisis lic={lic} onAnalyzed={fetchLic} analyzing={analyzing} setAnalyzing={setAnalyzing} />}
          {tab === "cobertura"  && <TabCobertura lic={lic} onAnalyzed={fetchLic} analyzing={analyzing} setAnalyzing={setAnalyzing} />}
          {tab === "innovacion" && <TabInnovacion lic={lic} />}
          {tab === "memoria"    && <TabMemoria lic={lic} onGenerated={fetchLic} />}
          {tab === "historico"  && <TabHistoricoMunicipio lic={lic} />}
          {tab === "chat"       && <ChatPanel lic={lic} />}
        </div>
      </div>
    </div>
  );
}
